import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import Box from '@mui/material/Box';
import { alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { type FC, type ReactElement, useEffect, useState } from 'react';
import { NAVY, RADIUS_COIN, STATUS_WARN, SURFACE_TINT, YELLOW } from 'styles/tokens';

interface Props {
  /** ISO timestamp / Date when the 24h window closes. `null` if no inbound message ever received. */
  windowExpiresAt: Date | null;
}

const RED = '#E7233B';
const HOUR_MS = 60 * 60 * 1000;

const formatRemaining = (ms: number): string => {
  const totalMinutes = Math.floor(ms / 60_000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours >= 1) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const formatAbsolute = (date: Date): string =>
  date.toLocaleString('es-CL', {
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

const WhatsAppWindowBadge: FC<Props> = ({ windowExpiresAt }) => {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const remainingMs = windowExpiresAt ? windowExpiresAt.getTime() - now : -1;
  const expired = !windowExpiresAt || remainingMs <= 0;

  let color: string;
  let bg: string;
  let icon: ReactElement;
  let label: string;
  let tooltip: string;

  if (expired) {
    color = alpha(NAVY, 0.45);
    bg = SURFACE_TINT;
    icon = <LockOutlinedIcon sx={{ fontSize: 14 }} />;
    label = 'Solo plantillas';
    tooltip = windowExpiresAt
      ? `Ventana de conversación abierta (24h) cerrada el ${formatAbsolute(windowExpiresAt)}. Solo se pueden enviar plantillas.`
      : 'Sin mensajes entrantes del cliente. Solo se pueden enviar plantillas.';
  } else if (remainingMs < HOUR_MS) {
    color = RED;
    bg = alpha(RED, 0.08);
    icon = <AccessTimeOutlinedIcon sx={{ fontSize: 14 }} />;
    label = formatRemaining(remainingMs);
    tooltip = `Ventana de conversación abierta (24h) cierra a las ${formatAbsolute(windowExpiresAt as Date)}.`;
  } else if (remainingMs < 6 * HOUR_MS) {
    color = STATUS_WARN;
    bg = alpha(YELLOW, 0.18);
    icon = <AccessTimeOutlinedIcon sx={{ fontSize: 14 }} />;
    label = formatRemaining(remainingMs);
    tooltip = `Ventana de conversación abierta (24h) cierra a las ${formatAbsolute(windowExpiresAt as Date)}.`;
  } else {
    color = alpha(NAVY, 0.7);
    bg = SURFACE_TINT;
    icon = <AccessTimeOutlinedIcon sx={{ fontSize: 14 }} />;
    label = formatRemaining(remainingMs);
    tooltip = `Ventana de conversación abierta (24h) cierra a las ${formatAbsolute(windowExpiresAt as Date)}.`;
  }

  return (
    <Tooltip title={tooltip} arrow>
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.25,
          borderRadius: RADIUS_COIN,
          backgroundColor: bg,
          color,
          cursor: 'help',
          userSelect: 'none',
        }}
      >
        {icon}
        <Typography
          sx={{
            fontFamily: 'Jost, sans-serif',
            fontSize: '0.75rem',
            fontWeight: 600,
            lineHeight: 1,
            color: 'inherit',
          }}
        >
          {label}
        </Typography>
      </Box>
    </Tooltip>
  );
};

export default WhatsAppWindowBadge;
